// The mini player (Oct 2026, user: "if you play a tune and go to a different
// page it plays in the background but it's not obvious how to go back"). The
// band keeps playing when you leave the workspace, so every other page shows
// a slim floating bar while a tune plays or is paused: the tune's name, which
// takes you back to the workspace it was playing in, Add to playlist (user:
// "add the currently playing tune to a playlist from the mini transport, so
// they can add before switching"), Pause/Resume and Stop.
// The full transport cannot come along: its chips and panels read the band
// hook the workspace owns. The tune is the store's, since loading another
// tune stops the band, so what is loaded is what is playing.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ListPlus } from 'lucide-react';
import { getChordPlayer } from '@/audio/player';
import { usePlaylistsAvailable } from '@/data/playlistAccess';
import { playlistRefKey } from '@/storage/playlists';
import { useTransportState } from '@/audio/useSequencePlaying';
import { useStore } from '@/state/store';
import { AddTuneToPlaylistPanel } from './AddTuneToPlaylist';
import { Modal } from './Modal';
import { TransportControlButton } from './TransportBar';
import { continueTuneWorkspaceHref, tuneWorkspaceKey } from './tuneWorkspace';
import { useOwnHeight } from './useOwnHeight';

export function MiniPlayer() {
  const transport = useTransportState();
  const title = useStore((s) => s.chart.title);
  const composer = useStore((s) => s.chart.composer);
  const scoreId = useStore((s) => tuneWorkspaceKey(s.score));
  // The Toast sits above it by the same measure as above the full bar.
  const measure = useOwnHeight('--mini-player-height', '--transport-height');
  const player = getChordPlayer();
  const playing = transport === 'playing';
  const name = title.trim() || 'Untitled';
  const playlists = usePlaylistsAvailable();
  const currentTuneRef = useStore((s) => s.currentTuneRef);
  // The dialog goes into the app shell, inside the visual style's scope.
  const [host, setHost] = useState<Element | null>(null);

  return (
    <div
      className="mini-player transport-surface"
      data-testid="mini-player"
      role="region"
      aria-label="Now playing"
      ref={measure}
    >
      <Link
        className="mini-player-tune"
        to={continueTuneWorkspaceHref(scoreId)}
        data-testid="mini-player-open"
        aria-label={`Back to ${name}`}
      >
        <span className="mini-player-title">{name}</span>
        <span className="mini-player-meta">
          {playing ? 'Playing' : 'Paused'}
          {composer ? ` · ${composer}` : ''}
        </span>
      </Link>
      <div className="mini-player-controls">
        {playlists && (
          <TransportControlButton
            aria-label={`Add ${name} to a playlist`}
            aria-haspopup="dialog"
            title="Add to playlist"
            data-testid="mini-player-add"
            onClick={(event) => setHost(event.currentTarget.closest('.app-shell') ?? document.body)}
          >
            <ListPlus aria-hidden="true" />
          </TransportControlButton>
        )}
        <TransportControlButton
          aria-label="Stop"
          data-testid="mini-player-stop"
          onClick={() => player.stop()}
        >
          <span className="transport-stop-icon" aria-hidden="true" />
        </TransportControlButton>
        <TransportControlButton
          variant="primary"
          aria-label={playing ? 'Pause' : 'Resume'}
          data-testid={playing ? 'mini-player-pause' : 'mini-player-resume'}
          onClick={() => (playing ? player.pause() : player.resumePlayback())}
        >
          <span
            className={`transport-primary-icon transport-primary-icon-${playing ? 'pause' : 'play'}`}
            aria-hidden="true"
          />
        </TransportControlButton>
      </div>
      {host &&
        createPortal(
          <Modal
            title="Add to playlist"
            onClose={() => setHost(null)}
            className="tile-playlist-dialog"
          >
            <p className="tile-playlist-tune">{name}</p>
            <AddTuneToPlaylistPanel
              key={currentTuneRef ? playlistRefKey(currentTuneRef) : 'unreferenced'}
              close={() => setHost(null)}
            />
          </Modal>,
          host,
        )}
    </div>
  );
}
